/* ==========================================================================
   Cursor sparkle — fairy-dust trail that follows the pointer.

   WHEN IT RUNS
   Only on devices with a real pointer, and only when the visitor has not asked
   for reduced motion. A trail is decoration; on touch there is no cursor to
   follow, and forcing motion on someone who has switched it off is worse than
   having no effect at all.

   COST
   Particles are spawned by distance travelled rather than per mousemove event,
   so a fast sweep across the page emits the same number as a slow one instead
   of one per event. The animation loop stops itself once the last particle
   dies and restarts on the next movement, so an idle tab costs nothing.

   Everything is drawn on one canvas fixed over the page with pointer-events
   off, so it can never intercept a click.
   ========================================================================== */
(function () {
  var root = document.querySelector('[data-cursor-sparkle]');
  if (!root) return;

  // A trail needs a pointer to trail behind, and must respect the OS setting.
  if (!window.matchMedia('(pointer: fine)').matches) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  var cfg = {
    colors: (root.dataset.colors || '#F0C320,#C9A227,#8540C3')
      .split(',')
      .map(function (c) { return c.trim(); })
      .filter(Boolean),
    shape: root.dataset.shape || 'sparkle',
    size: parseFloat(root.dataset.size) || 7,
    density: parseFloat(root.dataset.density) || 1,
    life: parseFloat(root.dataset.life) || 900,
    drift: parseFloat(root.dataset.drift) || 0.35,
    max: 240
  };

  var canvas = document.createElement('canvas');
  canvas.className = 'cursor-sparkle__canvas';
  root.appendChild(canvas);

  var ctx = canvas.getContext('2d');
  var dpr = Math.min(window.devicePixelRatio || 1, 2);
  var w = 0;
  var h = 0;

  function resize() {
    w = window.innerWidth;
    h = window.innerHeight;
    canvas.width = Math.floor(w * dpr);
    canvas.height = Math.floor(h * dpr);
    canvas.style.width = w + 'px';
    canvas.style.height = h + 'px';
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  resize();
  window.addEventListener('resize', resize, { passive: true });

  var particles = [];
  var running = false;
  var lastX = null;
  var lastY = null;
  var sinceSpawn = 0;
  var lastFrame = 0;

  function rand(min, max) {
    return min + Math.random() * (max - min);
  }

  function spawn(x, y, vx, vy) {
    if (particles.length >= cfg.max) return;
    particles.push({
      x: x + rand(-3, 3),
      y: y + rand(-3, 3),
      // Inherit a little of the cursor's momentum so the dust streams behind
      // the pointer rather than puffing out symmetrically.
      vx: vx * 0.05 + rand(-0.28, 0.28),
      vy: vy * 0.05 + rand(-0.35, 0.1),
      size: cfg.size * rand(0.45, 1),
      rot: rand(0, Math.PI * 2),
      spin: rand(-0.05, 0.05),
      life: cfg.life * rand(0.65, 1.15),
      age: 0,
      color: cfg.colors[(Math.random() * cfg.colors.length) | 0]
    });
  }

  function drawSparkle(p, r) {
    // Four-point star with concave sides — the classic pixie-dust glint.
    ctx.beginPath();
    ctx.moveTo(0, -r);
    ctx.quadraticCurveTo(0, 0, r, 0);
    ctx.quadraticCurveTo(0, 0, 0, r);
    ctx.quadraticCurveTo(0, 0, -r, 0);
    ctx.quadraticCurveTo(0, 0, 0, -r);
    ctx.closePath();
    ctx.fill();
  }

  function drawStar(p, r) {
    ctx.beginPath();
    for (var i = 0; i < 10; i++) {
      var radius = i % 2 === 0 ? r : r * 0.42;
      var a = (Math.PI / 5) * i - Math.PI / 2;
      var px = Math.cos(a) * radius;
      var py = Math.sin(a) * radius;
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.closePath();
    ctx.fill();
  }

  function drawCircle(p, r) {
    ctx.beginPath();
    ctx.arc(0, 0, r * 0.72, 0, Math.PI * 2);
    ctx.fill();
  }

  var draw = cfg.shape === 'star' ? drawStar : cfg.shape === 'circle' ? drawCircle : drawSparkle;

  function frame(now) {
    var dt = lastFrame ? Math.min(now - lastFrame, 50) : 16;
    lastFrame = now;

    ctx.clearRect(0, 0, w, h);

    for (var i = particles.length - 1; i >= 0; i--) {
      var p = particles[i];
      p.age += dt;

      if (p.age >= p.life) {
        particles.splice(i, 1);
        continue;
      }

      var t = p.age / p.life;
      p.x += p.vx * (dt / 16);
      p.y += (p.vy + cfg.drift * t) * (dt / 16);
      p.rot += p.spin * (dt / 16);

      // Fade in briefly, then out — a particle that appears at full opacity
      // reads as a hard dot rather than a glint catching the light.
      var alpha = t < 0.15 ? t / 0.15 : 1 - (t - 0.15) / 0.85;

      ctx.save();
      ctx.globalAlpha = Math.max(0, alpha);
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      ctx.fillStyle = p.color;
      draw(p, p.size * (1 - t * 0.55));
      ctx.restore();
    }

    if (particles.length) {
      window.requestAnimationFrame(frame);
    } else {
      // Nothing left to animate; the next pointer move restarts the loop.
      running = false;
      lastFrame = 0;
    }
  }

  function start() {
    if (running) return;
    running = true;
    lastFrame = 0;
    window.requestAnimationFrame(frame);
  }

  window.addEventListener(
    'pointermove',
    function (e) {
      if (e.pointerType === 'touch') return;

      if (lastX === null) {
        lastX = e.clientX;
        lastY = e.clientY;
        return;
      }

      var dx = e.clientX - lastX;
      var dy = e.clientY - lastY;
      var dist = Math.sqrt(dx * dx + dy * dy);

      lastX = e.clientX;
      lastY = e.clientY;
      sinceSpawn += dist;

      // Spawn per distance travelled, not per event: a fast sweep and a slow
      // drag then leave the same density of dust behind them.
      var every = 14 / cfg.density;
      while (sinceSpawn >= every) {
        sinceSpawn -= every;
        spawn(e.clientX, e.clientY, dx, dy);
      }

      if (particles.length) start();
    },
    { passive: true }
  );

  // Leaving the window should not strand a frozen trail on screen.
  document.addEventListener('visibilitychange', function () {
    if (document.hidden) {
      particles.length = 0;
      ctx.clearRect(0, 0, w, h);
    }
  });
})();
